import React from "react";
import { Box, Typography, Button, Skeleton } from "@mui/material";
import { FormattedMessage } from "react-intl";
import { useSelector } from "react-redux";
import { truncate } from "bundles/common/utils/utils";
import ProfilePicture from "bundles/common/components/ProfilePicture";
import Congrats from "icons/congrats.svg";

const StepCongrats = ({ recommendations, loading = false }) => {
  const i18nLocale = useSelector((state) => state.i18nLocale);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "1.5rem",
      }}
    >
      <Box
        component="img"
        src={Congrats}
        alt="Congrats"
        sx={{ maxWidth: "180px", maxHeight: "180px" }}
      />

      <Typography
        sx={{
          fontFamily: "'Segoe UI Semibold', system-ui, sans-serif",
          fontSize: "1rem",
          color: "hsl(222.2, 84%, 4.9%)",
          textAlign: "center",
        }}
      >
        {!loading && recommendations.length === 0 ? (
          <FormattedMessage
            id="onboarding.selection_empty"
            defaultMessage="Ton profil est prêt ! Découvre les offres qui t'attendent."
          />
        ) : (
          <FormattedMessage id="onboarding.selection" />
        )}
      </Typography>

      {(loading || recommendations.length > 0) && (
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: "1rem",
            width: "100%",
          }}
        >
          {loading
            ? [0, 1, 2].map((i) => (
              <Box
                key={i}
                sx={{
                  flex: 1,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  p: "1rem",
                  borderRadius: "0.75rem",
                  border: "1px solid hsl(214.3, 31.8%, 91.4%)",
                }}
              >
                <Box sx={{ flex: 1, mr: "1rem" }}>
                  <Skeleton variant="text" width="70%" height={22} />
                  <Skeleton variant="text" width="45%" height={18} />
                </Box>
                <Skeleton variant="rounded" width={48} height={48} sx={{ borderRadius: "6px" }} />
              </Box>
            ))
            : recommendations.map((offer) => (
              <Box
                key={offer.id}
                component="a"
                href={`/${i18nLocale}/candidate?tab=jobs&jid=${offer.id}`}
                sx={{
                  flex: 1,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  p: "1rem",
                  borderRadius: "0.75rem",
                  bgcolor: "hsl(0, 0%, 100%)",
                  border: "1px solid hsl(214.3, 31.8%, 91.4%)",
                  textDecoration: "none",
                  color: "hsl(222.2, 84%, 4.9%)",
                  transition: "box-shadow 0.2s, border-color 0.2s",
                  "&:hover": {
                    borderColor: "hsl(153, 68%, 25%)",
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
                  },
                }}
              >
                <Box>
                  <Typography
                    sx={{
                      fontFamily: "'Segoe UI Semibold', system-ui, sans-serif",
                      fontSize: "0.875rem",
                    }}
                  >
                    {truncate(offer.job_title, 30)}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: "'Segoe UI', system-ui, sans-serif",
                      fontSize: "0.8125rem",
                      color: "hsl(215.4, 16.3%, 46.9%)",
                    }}
                  >
                    {offer.company.name}
                  </Typography>
                </Box>
                <ProfilePicture
                  picture={offer?.company?.logo_url}
                  size={48}
                  initials={offer?.company?.name[0]}
                  alt={`Logo ${offer?.company?.name}`}
                  borderRadius="6px"
                />
              </Box>
            ))}
        </Box>
      )}

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          gap: "1rem",
          width: "100%",
          mt: "0.5rem",
        }}
      >
        <Button
          fullWidth
          href={`/${i18nLocale}/candidate?tab=jobs`}
          sx={{
            height: "3rem",
            fontSize: "0.875rem",
            fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
            letterSpacing: "0.05em",
            textTransform: "uppercase",
            borderRadius: "9999px",
            bgcolor: "hsl(153, 68%, 25%)",
            color: "hsl(0, 0%, 100%)",
            "&:hover": { bgcolor: "hsl(153, 68%, 22%)" },
          }}
        >
          <FormattedMessage id="onboarding.find_job" />
        </Button>
        <Button
          fullWidth
          href={`/${i18nLocale}/candidate?tab=profile`}
          sx={{
            height: "3rem",
            fontSize: "0.875rem",
            fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
            letterSpacing: "0.05em",
            textTransform: "uppercase",
            borderRadius: "9999px",
            border: "1px solid hsl(153, 68%, 25%)",
            color: "hsl(153, 68%, 25%)",
            bgcolor: "transparent",
            "&:hover": { bgcolor: "hsl(153, 68%, 25%)", color: "hsl(0, 0%, 100%)" },
          }}
        >
          <FormattedMessage id="onboarding.complete_profile" />
        </Button>
      </Box>
    </Box>
  );
};

export default StepCongrats;
